import React, { useState, useEffect } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import RefreshIcon from '@mui/icons-material/Refresh';
import FeedIcon from '@mui/icons-material/Feed';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import '../shared.css';

function CutomerDetails() {
    const [dataItem, setDataItem] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const { isAuthenticated, companyId, branchId, permissions, token, logout } = useAuth();

    const [totalQtyBilled, setTotalQtyBilled] = useState(0);
    const [totalMRPBilled, setTotalMRPBilled] = useState(0);
    const [totalTotalBillAmt, setTotalTotalBillAmt] = useState(0);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    useEffect(() => {
        let QtyBilled = 0, MRPBilled = 0, TotalBillAmt = 0;
        dataItem.forEach(item => {
            QtyBilled += Number(item?.QtyBilled ?? 0);
            MRPBilled += Number(item?.MRPBilled ?? 0);
            TotalBillAmt += Number(item?.TotalBillAmt ?? 0);
        });
        setTotalQtyBilled(QtyBilled.toFixed(2));
        setTotalMRPBilled(MRPBilled.toFixed(2));
        setTotalTotalBillAmt(TotalBillAmt.toFixed(2));
    }, [dataItem]);

    const getFormattedCurrentDate = () => new Date().toISOString().split('T')[0];
    const getFormattedStartDate = () => {
        const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0];
    };

    const hasReadReportPermission = permissions.includes('Read Reports');
    const [loading, setLoading] = useState(false);

    const fetchTableData = async () => {
        if (hasReadReportPermission) {
            setDataItem([]);
            setLoading(true);
            try {
                const response = await fetchData('ReportApi', `/CustomerDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
                if (response.responseCode === '1111') {
                    setDataItem(response.response_data);
                }
            } catch (error) {
                console.error('Error fetching Customer Details:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    const filteredDataItem = dataItem.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-GB', options);
    };

    const handleExportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Customer Data');

        const rightAlignColumns = ['Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %'];

        const headerCaption = `Customer Data From : ${formatDate(fromDate)} To : ${formatDate(toDate)}`;
        worksheet.mergeCells('A1:N1');
        const headerCaptionCell = worksheet.getCell('A1');
        headerCaptionCell.value = headerCaption;
        headerCaptionCell.font = { bold: true, size: 14 };
        headerCaptionCell.alignment = { vertical: 'middle', horizontal: 'left' };

        const headerRowValues = [
            'Bill Number', 'Bill Date', 'Bill Time', 'Customer Name', 'Contact Number', 'Customer Address',
            'Qty Billed', 'Total MRP Billed', 'Net Sales Value + Tax Billed', 'Total Discount %',
            'Additional Disc Type', 'SC Membership Number', 'Voucher Number', 'Sales Man'
        ];
        const headerRow = worksheet.addRow(headerRowValues);
        headerRow.eachCell((cell) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFB6D7A8' } };
            cell.font = { bold: true, color: { argb: 'FF000000' } };
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
        worksheet.columns = headerRowValues.map(() => ({ width: 18 }));

        filteredDataItem.forEach((rowData) => {
            const dataRow = [
                rowData.BillNumber, formatDate(rowData.BillDate), rowData.BillTime,
                rowData.CustomerName, rowData.ContactNumber, rowData.CustomerAddress,
                parseFloat(rowData.QtyBilled), parseFloat(rowData.MRPBilled),
                parseFloat(rowData.TotalBillAmt), parseFloat(rowData.TotalDiscPer),
                rowData.AdditionalDiscountType, rowData.SCMembershipNumber,
                rowData.VoucherNumber, rowData.SalesManName
            ];
            const row = worksheet.addRow(dataRow);
            row.eachCell((cell, colNumber) => {
                const headerName = headerRowValues[colNumber - 1];
                if (rightAlignColumns.includes(headerName)) {
                    cell.alignment = { vertical: 'middle', horizontal: 'right' };
                } else {
                    cell.alignment = { vertical: 'middle', horizontal: 'center' };
                }
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });

        const totalRowValues = [
            '', '', '', '', '', 'Total',
            parseFloat(totalQtyBilled), parseFloat(totalMRPBilled), parseFloat(totalTotalBillAmt),
            '', '', '', '', ''
        ];
        const totalRow = worksheet.addRow(totalRowValues);
        totalRow.font = { bold: true };
        totalRow.eachCell((cell, colNumber) => {
            const headerName = headerRowValues[colNumber - 1];
            if (rightAlignColumns.includes(headerName)) {
                cell.alignment = { vertical: 'middle', horizontal: 'right' };
            } else {
                cell.alignment = { vertical: 'middle', horizontal: 'center' };
            }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2CC' } };
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), 'Customer_Data.xlsx');
    };

    return (
        <div className="pg-container">
            <div className="pg-header">
                <h5 className="pg-title">Customer Data</h5>
                <div className="pg-header-actions">
                    <div className="pg-date-row">
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>From Date</label>
                            <input className="pg-input" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="pg-field">
                            <label className="pg-label" style={{ background: 'var(--pg-bg)' }}>To Date</label>
                            <input className="pg-input" type="date" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        <button className="pg-btn pg-btn-outline" onClick={fetchTableData} disabled={loading}>
                            <RefreshIcon /> Refresh
                        </button>
                    </div>
                    <div className="pg-action-row">
                        <button className="pg-btn pg-btn-success" onClick={handleExportExcel}>
                            <FeedIcon /> Excel
                        </button>
                        <button className="pg-btn pg-btn-outline" onClick={() => setShowSearch(v => !v)}>
                            <SearchIcon /> Search
                        </button>
                    </div>
                    {showSearch && (
                        <input className="pg-input pg-search-input" placeholder="Search…"
                            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus />
                    )}
                </div>
            </div>

            {loading && (
                <div className="pg-loading-overlay">
                    <div className="pg-loading-card">
                        <div className="pg-logo-loader">
                            <div className="pg-logo-block pg-block-green"></div>
                            <div className="pg-logo-block pg-block-blue"></div>
                            <div className="pg-logo-block pg-block-red"></div>
                            <div className="pg-logo-block pg-block-yellow"></div>
                        </div>
                        <p className="pg-loading-text">Loading...</p>
                    </div>
                </div>
            )}

            <div className="pg-table-wrap">
                <table className="pg-table">
                    <thead>
                        <tr>
                            <th>Bill Number</th>
                            <th>Bill Date</th>
                            <th>Bill Time</th>
                            <th>Customer Name</th>
                            <th>Contact Number</th>
                            <th>Customer Address</th>
                            <th style={{ textAlign: 'right' }}>Qty Billed</th>
                            <th style={{ textAlign: 'right' }}>Total MRP Billed</th>
                            <th style={{ textAlign: 'right' }}>Net Sales Value + Tax Billed</th>
                            <th style={{ textAlign: 'right' }}>Total Discount %</th>
                            <th>Addt'l. DISC Type</th>
                            <th>SC MembershipNumber</th>
                            <th>Voucher Number</th>
                            <th>Sales Man</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredDataItem.length === 0
                            ? <tr><td colSpan={14} style={{ textAlign: 'center', color: '#94a3b8', padding: 16 }}>No records found</td></tr>
                            : filteredDataItem.map((data, index) => (
                                <tr key={index}>
                                    <td>{data.BillNumber}</td>
                                    <td>{formatDate(data.BillDate)}</td>
                                    <td>{data.BillTime}</td>
                                    <td>{data.CustomerName}</td>
                                    <td>{data.ContactNumber}</td>
                                    <td>{data.CustomerAddress}</td>
                                    <td style={{ textAlign: 'right' }}>{Number(data?.QtyBilled ?? 0).toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{Number(data?.MRPBilled ?? 0).toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{Number(data?.TotalBillAmt ?? 0).toFixed(2)}</td>
                                    <td style={{ textAlign: 'right' }}>{Number(data?.TotalDiscPer ?? 0).toFixed(2)}</td>
                                    <td>{data?.AdditionalDiscountType ?? ''}</td>
                                    <td>{data.SCMembershipNumber}</td>
                                    <td>{data.VoucherNumber}</td>
                                    <td>{data.SalesManName}</td>
                                </tr>
                            ))
                        }
                        <tr style={{ background: 'linear-gradient(135deg, #1c2a3a 0%, #243447 100%)', color: '#e8f0f8', fontWeight: 700 }}>
                            <td colSpan={6} style={{ textAlign: 'right', padding: '6px 10px' }}>Total</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalQtyBilled}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalMRPBilled}</td>
                            <td style={{ textAlign: 'right', padding: '6px 10px' }}>{totalTotalBillAmt}</td>
                            <td colSpan={5} style={{ padding: '6px 10px' }}></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pg-watermark"><CopyrightIcon style={{ fontSize: 12 }} /><b>EFFTABS SOLUTIONS LLP</b></div>
        </div>
    );
}

export default CutomerDetails;
